/**
 * WCO-2465 — map a backend scan error CODE (+ data) to a localized toast string.
 *
 * The Scan Production Outputs backend returns `{ code, data }` on classifiable failures so the
 * UI can show a message in the user's language (English `message` remains the fallback). Codes
 * mirror services/inventory-service/src/utils/scanErrorCodes.ts.
 */

type TFunc = (key: string, opts?: Record<string, any>) => string;

// Kept in sync with the backend SCAN_ERR constants.
const CODE = {
  WRONG_TARGET_OUTPUT: 'SCAN_WRONG_TARGET_OUTPUT',
  WRONG_TARGET_TRANSFER: 'SCAN_WRONG_TARGET_TRANSFER',
  WRONG_DAY: 'SCAN_WRONG_DAY',
  UNRECOGNIZED: 'SCAN_UNRECOGNIZED',
} as const;

/**
 * Returns a localized message for a known code, or `undefined` if the code is unknown (caller
 * should then fall back to the backend's English message).
 */
export function scanErrorMessage(
  t: TFunc,
  code: string | undefined,
  data: Record<string, any> = {},
): string | undefined {
  switch (code) {
    case CODE.WRONG_TARGET_OUTPUT: {
      // Prefer the kind-specific string ('finished' | 'byproduct' | 'fpni'); fall back to the generic.
      const kind = data?.kind;
      if (kind === 'finished') return t('shipOutScan.errWrongTargetOutput_finished');
      if (kind === 'byproduct') return t('shipOutScan.errWrongTargetOutput_byproduct');
      if (kind === 'fpni') return t('shipOutScan.errWrongTargetOutput_fpni');
      return t('shipOutScan.errWrongTargetOutput');
    }
    case CODE.WRONG_TARGET_TRANSFER:
      return t('shipOutScan.errWrongTargetTransfer');
    case CODE.WRONG_DAY:
      return t('shipOutScan.errWrongDay', { otherDate: data?.otherDate ?? '' });
    case CODE.UNRECOGNIZED:
      return t('shipOutScan.errUnrecognized');
    default:
      return undefined;
  }
}
